import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Grid,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { Link } from "react-router-dom";
import SaveIcon from "@mui/icons-material/Save";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import { toast } from "react-toastify";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import IntegrationQueueTable from "../../components/IntegrationQueueTable";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const PROVIDER_LABELS = {
  openai: "API Key OpenAI",
  anthropic: "API Key Anthropic (Claude)",
  gemini: "API Key Google (Gemini)",
};

const ChatGPTIntegration = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [provider, setProvider] = useState("openai");
  const [providers, setProviders] = useState([]);
  const [apiKey, setApiKey] = useState("");
  const [model, setModel] = useState("gpt-4o-mini");
  const [systemPrompt, setSystemPrompt] = useState("");
  const [queues, setQueues] = useState([]);
  const [enabledQueueIds, setEnabledQueueIds] = useState([]);
  const [respondWithAssignee, setRespondWithAssignee] = useState(false);

  const models = useMemo(() => {
    const found = providers.find(item => item.id === provider);
    return found?.models || [];
  }, [providers, provider]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/integrations/chatgpt");
        setProviders(data.providers || []);
        setProvider(data.provider || "openai");
        setApiKey(data.apiKey || "");
        setModel(data.model || "gpt-4o-mini");
        setSystemPrompt(data.systemPrompt || "");
        setQueues(data.queues || []);
        setEnabledQueueIds(data.enabledQueueIds || []);
        setRespondWithAssignee(data.respondWithAssignee === "enabled");
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleProviderChange = value => {
    setProvider(value);
    const found = providers.find(item => item.id === value);
    const nextModels = found?.models || [];
    if (nextModels.length && !nextModels.includes(model)) {
      setModel(nextModels[0]);
    }
  };

  const handleToggleQueue = (queueId, checked) => {
    setEnabledQueueIds(prev => {
      const id = Number(queueId);
      if (checked) return prev.includes(id) ? prev : [...prev, id];
      return prev.filter(item => item !== id);
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/integrations/chatgpt", {
        provider,
        apiKey,
        model,
        systemPrompt,
        respondWithAssignee: respondWithAssignee ? "enabled" : "disabled",
        enabledQueueIds,
      });
      setProvider(data.provider || provider);
      setModel(data.model || model);
      setEnabledQueueIds(data.enabledQueueIds || []);
      setRespondWithAssignee(data.respondWithAssignee === "enabled");
      toast.success("Integração de IA salva com sucesso.");
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <MainContainer>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={320}>
          <CircularProgress color="primary" />
        </Box>
      </MainContainer>
    );
  }

  return (
    <MainContainer>
      <MainHeader>
        <Title>Assistente IA</Title>
        <MainHeaderButtonsWrapper>
          <Button variant="outlined" component={Link} to="/integracoes">
            Voltar
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
            disabled={saving}
            onClick={handleSave}
          >
            Salvar
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Box sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
        <Grid container spacing={2} sx={{ minHeight: 0 }}>
          <Grid item xs={12} lg={5} sx={{ display: "flex", minHeight: 0 }}>
            <Paper
              variant="outlined"
              sx={{
                p: 2.5,
                width: "100%",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Typography variant="subtitle1" sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2, fontWeight: 600 }}>
                <SmartToyIcon color="primary" />
                Configuração global
              </Typography>
              <Stack spacing={2}>
                <TextField
                  select
                  fullWidth
                  label="Provedor"
                  value={provider}
                  onChange={e => handleProviderChange(e.target.value)}
                >
                  {(providers.length
                    ? providers
                    : [
                        { id: "openai", label: "OpenAI (ChatGPT)" },
                        { id: "anthropic", label: "Anthropic (Claude)" },
                        { id: "gemini", label: "Google (Gemini)" },
                      ]
                  ).map(item => (
                    <MenuItem key={item.id} value={item.id}>
                      {item.label}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  fullWidth
                  type="password"
                  label={PROVIDER_LABELS[provider] || "API Key"}
                  value={apiKey}
                  onChange={e => setApiKey(e.target.value)}
                  placeholder="..."
                />
                <TextField
                  select
                  fullWidth
                  label="Modelo"
                  value={models.includes(model) ? model : models[0] || ""}
                  onChange={e => setModel(e.target.value)}
                >
                  {models.map(item => (
                    <MenuItem key={item} value={item}>{item}</MenuItem>
                  ))}
                </TextField>
                <TextField
                  fullWidth
                  multiline
                  rows={12}
                  label="Prompt do sistema"
                  value={systemPrompt}
                  onChange={e => setSystemPrompt(e.target.value)}
                  placeholder="Você é um assistente de suporte da Life-Sip Telecom..."
                  helperText={`${(systemPrompt || "").length.toLocaleString("pt-BR")} caracteres · suporte a prompts longos (TEXT)`}
                  FormHelperTextProps={{ sx: { mx: 0 } }}
                  inputProps={{
                    maxLength: 100000,
                    style: {
                      overflowY: "auto",
                      resize: "vertical",
                      maxHeight: 360,
                      fontFamily:
                        "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                      fontSize: 13,
                      lineHeight: 1.45,
                    },
                  }}
                  sx={{
                    "& .MuiInputBase-root": {
                      alignItems: "flex-start",
                      overflow: "hidden",
                    },
                    "& textarea": {
                      overflowY: "auto !important",
                      maxHeight: "360px !important",
                    },
                  }}
                />
                <FormControlLabel
                  control={
                    <Switch
                      color="primary"
                      checked={respondWithAssignee}
                      onChange={e => setRespondWithAssignee(e.target.checked)}
                    />
                  }
                  label="Responder mesmo com atendente atribuído"
                />
                <Typography variant="body2" color="textSecondary">
                  Por padrão, a IA responde em tickets aguardando ou em aberto sem
                  responsável. Com esta opção ativa, continua respondendo mesmo após um
                  atendente aceitar o ticket. Typebot/Dialogflow ativo na mesma fila tem
                  prioridade. O prompt aceita textos longos para instruções detalhadas
                  (OpenAI, Claude e Gemini).
                </Typography>
              </Stack>
            </Paper>
          </Grid>

          <Grid item xs={12} lg={7} sx={{ display: "flex", minHeight: 0 }}>
            <IntegrationQueueTable
              queues={queues}
              enabledQueueIds={enabledQueueIds}
              onToggle={handleToggleQueue}
              title="Ativação por fila"
              description="Ative o assistente de IA individualmente em cada departamento."
            />
          </Grid>
        </Grid>
      </Box>
    </MainContainer>
  );
};

export default ChatGPTIntegration;
